import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { AgronodCard } from '@agronod/mui-components';
import {
  avvikelseText,
  mattKolumner,
  modellLabel,
  modellRad,
  RevisionGard,
  RevisionKolumn,
  RevisionModell,
} from '../../utils/api';
import { GardCell } from './GardTabell';
import { MattRubrik } from './MattRubrik';
import { CellStatus, StatusCell } from './StatusCell';
import { rubrikSx } from './tabellStil';

interface Props {
  gardar: RevisionGard[];
  modeller: RevisionModell[];
}

interface Kolumngrupp {
  modell: string;
  kolumner: RevisionKolumn[];
}

/**
 * Gemensam, passiv lista över gårdarna som inte valdes ut i ett låst urval
 * (kravspec v4 C.5): pseudonymiserade, nedtonade, utan länkar. Alla
 * modellers mått visas som kolumngrupper – där modellen inte bedömer gården
 * visas streck. Sorterad efter gårdens största avvikelse.
 */
export const EjUtvaldaTabell = ({ gardar, modeller }: Props) => {
  const grupper: Kolumngrupp[] = modeller
    .map((m) => ({
      modell: m.modell,
      kolumner: mattKolumner(gardar, m.modell, m.matt),
    }))
    .filter((g) => g.kolumner.length > 0);

  const storstaAvvikelse = (gard: RevisionGard) =>
    gard.modeller.length > 0
      ? Math.max(...gard.modeller.map((m) => m.absDifferens))
      : null;

  const rader = [...gardar].sort(
    (a, b) =>
      (storstaAvvikelse(b) ?? -1) - (storstaAvvikelse(a) ?? -1) ||
      a.nummer - b.nummer,
  );

  const status = (gard: RevisionGard, modell: string, falt: string): CellStatus | undefined => {
    const rad = modellRad(gard, modell);
    if (!rad) {
      return 'ej_bedomd';
    }
    return rad.matt.find((m) => m.falt === falt)?.status;
  };

  return (
    <AgronodCard sx={{ p: 0 }}>
      <TableContainer>
        <Table size="small" aria-label="Ej utvalda för revision">
          <TableHead>
            <TableRow>
              <TableCell colSpan={2} sx={{ borderBottom: 0 }} />
              {grupper.map((g) => (
                <TableCell
                  key={g.modell}
                  colSpan={g.kolumner.length}
                  align="center"
                  sx={{ ...rubrikSx, pb: 0, borderBottom: 0 }}
                >
                  {modellLabel(g.modell)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...rubrikSx, minWidth: 160 }}>Gård</TableCell>
              <TableCell align="right" sx={{ ...rubrikSx, whiteSpace: 'nowrap' }}>
                Största avvikelse
              </TableCell>
              {grupper.flatMap((g) =>
                g.kolumner.map((m) => (
                  <MattRubrik key={`${g.modell}-${m.falt}`} kolumn={m} />
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rader.map((gard) => {
              const avvikelse = storstaAvvikelse(gard);
              return (
                <TableRow
                  key={gard.nummer}
                  sx={{ bgcolor: 'gray50', opacity: 0.55, cursor: 'default' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <GardCell gard={gard} />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {avvikelse !== null ? avvikelseText(avvikelse) : ''}
                  </TableCell>
                  {grupper.flatMap((g) =>
                    g.kolumner.map((m) => (
                      <TableCell key={`${g.modell}-${m.falt}`} align="center" sx={{ px: 0.75 }}>
                        <StatusCell status={status(gard, g.modell, m.falt)} />
                      </TableCell>
                    )),
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </AgronodCard>
  );
};
